import { useMemo, useCallback } from 'react';

const getStorageKey = (storageKey, scope) => `${storageKey}:${scope}`;

const readDraft = (storageKey, scope) => {
  if (typeof window === 'undefined' || !storageKey || !scope) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(getStorageKey(storageKey, scope));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeDraft = (storageKey, scope, values, fields) => {
  if (typeof window === 'undefined' || !storageKey || !scope) {
    return;
  }

  const draft = fields.reduce((acc, field) => {
    acc[field] = values?.[field] ?? '';
    return acc;
  }, {});

  const hasContent = fields.some(field => draft[field]);

  if (!hasContent) {
    window.localStorage.removeItem(getStorageKey(storageKey, scope));
    return;
  }

  window.localStorage.setItem(getStorageKey(storageKey, scope), JSON.stringify(draft));
};

const removeDraft = (storageKey, scope) => {
  if (typeof window === 'undefined' || !storageKey || !scope) {
    return;
  }

  window.localStorage.removeItem(getStorageKey(storageKey, scope));
};

const mergeDraftIntoInitialValues = (initialValues, draft, fields) => {
  if (!draft) {
    return initialValues;
  }

  const merged = { ...initialValues };

  fields.forEach(field => {
    const draftValue = draft[field];
    if (draftValue) {
      merged[field] = draftValue;
    }
  });

  return merged;
};

/**
 * Persists selected form fields to localStorage and restores them on mount.
 *
 * @param {Object} options
 * @param {string} options.storageKey - Namespace prefix for localStorage
 * @param {string} options.scope - Unique id for this form instance (e.g. listing + entity id)
 * @param {string[]} options.fields - Field names to save and restore
 * @param {Object} options.initialValues - Base initial values from the server
 */
const useFormDraft = ({ storageKey, scope, fields, initialValues }) => {
  const mergedInitialValues = useMemo(() => {
    const draft = readDraft(storageKey, scope);
    return mergeDraftIntoInitialValues(initialValues, draft, fields);
  }, [storageKey, scope, initialValues, fields]);

  const saveDraft = useCallback(
    values => {
      writeDraft(storageKey, scope, values, fields);
    },
    [storageKey, scope, fields]
  );

  const clearDraft = useCallback(() => {
    removeDraft(storageKey, scope);
  }, [storageKey, scope]);

  return {
    mergedInitialValues,
    saveDraft,
    clearDraft,
  };
};

export default useFormDraft;
